import React, { useEffect, useState } from "react";
import {
  Box, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import EnTetePage from "../../components/charte/EnTetePage";
import Bloc from "../../components/charte/Bloc";
import TitreSection from "../../components/charte/TitreSection";
import { BoutonPrincipal, BoutonSecondaire } from "../../components/charte/Boutons";
import { AIDE_RESEAU } from "../../aide/textesAide";
import { STYLE_TON } from "../../theme/tribeTheme";
import type { TonStatut } from "../../utilities/statutsQuete";
import { getUser } from "../../utilities/adminservice";
import { messageServeur } from "../../utilities/userService";
import {
  annulerInvitation, creerInvitation, EtatInvitation, InvitationClient, InvitationCreee, listerInvitations,
} from "../../utilities/invitationsClient";
import { Erreur, Pastille } from "../facturation/composantsFacturation";

/* Réseau › Clients invités : le portail client (client.tribe.sn) n'ouvre un
   compte que sur invitation. Créer une invitation (email, organisation),
   suivre son état, annuler une invitation non utilisée. Réservé au droit
   d'écriture « Restrictions d'accès » (accessrestriction_write), que
   user-service vérifie aussi de son côté. */

const ETATS: Record<EtatInvitation, { libelle: string; ton: TonStatut }> = {
  EN_ATTENTE: { libelle: "En attente", ton: "attente" },
  UTILISEE: { libelle: "Compte créé", ton: "succes" },
  EXPIREE: { libelle: "Expirée", ton: "neutre" },
  ANNULEE: { libelle: "Annulée", ton: "erreur" },
};

const jour = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ClientsInvites() {
  const [droit, setDroit] = useState<boolean | null>(null);
  const [invitations, setInvitations] = useState<InvitationClient[]>([]);
  const [chargees, setChargees] = useState(false);
  const [erreurListe, setErreurListe] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [organisation, setOrganisation] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [creee, setCreee] = useState<InvitationCreee | null>(null);
  const [copie, setCopie] = useState(false);

  const [aAnnuler, setAAnnuler] = useState<InvitationClient | null>(null);
  const [erreurAnnulation, setErreurAnnulation] = useState<string | null>(null);

  const charger = () =>
    listerInvitations()
      .then((l) => { setInvitations(l); setErreurListe(null); })
      .catch((e) => setErreurListe(messageServeur(e, "Liste des invitations illisible.")))
      .finally(() => setChargees(true));

  useEffect(() => {
    getUser()
      .then((u: any) => {
        const ok = !!u?.role_type?.accessrestriction_write;
        setDroit(ok);
        if (ok) charger();
      })
      .catch(() => setDroit(false));
  }, []);

  const inviter = async () => {
    setErreur(null);
    setCreee(null);
    setCopie(false);
    if (!EMAIL.test(email.trim())) { setErreur("Adresse email invalide."); return; }
    setEnvoi(true);
    try {
      const r = await creerInvitation(email.trim(), organisation.trim());
      setCreee(r);
      setEmail("");
      setOrganisation("");
      charger();
    } catch (e: any) {
      setErreur(messageServeur(e, "L'invitation n'a pas pu être créée."));
    } finally {
      setEnvoi(false);
    }
  };

  const copier = async () => {
    if (!creee) return;
    try { await navigator.clipboard.writeText(creee.lien); setCopie(true); } catch { setCopie(false); }
  };

  const annuler = async () => {
    if (!aAnnuler) return;
    setErreurAnnulation(null);
    try {
      await annulerInvitation(aAnnuler.id);
      setAAnnuler(null);
      charger();
    } catch (e: any) {
      setErreurAnnulation(messageServeur(e, "L'invitation n'a pas pu être annulée."));
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3.5 }, maxWidth: 1280 }}>
      <EnTetePage domaine="reseau" titre="Clients invités" aide={AIDE_RESEAU.clientsInvites}
        phrase="Ouvrir l'accès d'un client au portail client.tribe.sn : une invitation par adresse, valable 7 jours, à usage unique." />

      {droit === null ? null : !droit ? (
        <Bloc>
          <Typography sx={{ fontSize: 14 }}>
            Cette page est réservée aux administrateurs dont le rôle a le droit d'écriture « Restrictions d'accès ».
            Demandez ce droit à un super admin.
          </Typography>
        </Bloc>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Bloc>
            <TitreSection titre="Inviter un client" />
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-start", mt: 1.5 }}>
              <TextField size="small" label="Adresse email du client" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} sx={{ minWidth: 280, flex: 1 }} />
              <TextField size="small" label="Organisation" value={organisation} onChange={(e) => setOrganisation(e.target.value)}
                helperText="Elle fera foi sur le compte : écrivez-la comme sur les documents." sx={{ minWidth: 260, flex: 1 }} />
              <BoutonPrincipal onClick={inviter} disabled={envoi || !email.trim()}>
                {envoi ? "Envoi…" : "Envoyer l'invitation"}
              </BoutonPrincipal>
            </Box>
            <Erreur message={erreur} />
            {creee && (
              <Box role="status" sx={{ mt: 2, p: 1.5, border: "var(--trait-fin)", borderLeft: `4px solid ${creee.courrielEnvoye ? STYLE_TON.succes.color : STYLE_TON.attente.color}`, borderRadius: "6px" }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  {creee.courrielEnvoye
                    ? `Invitation envoyée à ${creee.email}, valable jusqu'au ${jour(creee.expireLe)}.`
                    : `Invitation créée pour ${creee.email}, mais le courriel n'est pas parti : transmettez-lui le lien ci-dessous.`}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "var(--c-text-secondary)", mt: 0.75 }}>
                  Le lien n'est affiché qu'une fois. Copiez-le maintenant si vous devez le transmettre vous-même.
                </Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1, flexWrap: "wrap" }}>
                  <Typography component="code" sx={{ fontSize: 12.5, wordBreak: "break-all", flex: 1, minWidth: 240 }}>{creee.lien}</Typography>
                  <BoutonSecondaire size="small" onClick={copier}>{copie ? "Lien copié" : "Copier le lien"}</BoutonSecondaire>
                </Box>
              </Box>
            )}
          </Bloc>

          <Bloc>
            <TitreSection titre="Invitations" aide={AIDE_RESEAU.etatsInvitation} />
            <Erreur message={erreurListe} />
            {!chargees ? null : invitations.length === 0 && !erreurListe ? (
              <Typography sx={{ fontSize: 14, color: "var(--c-text-secondary)", mt: 1 }}>Aucune invitation pour l'instant.</Typography>
            ) : (
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>{["Client", "Organisation", "État", "Créée", "Expire le", ""].map((h) => <TableCell key={h}>{h}</TableCell>)}</TableRow>
                </TableHead>
                <TableBody>
                  {invitations.map((i) => {
                    const e = ETATS[i.etat] ?? { libelle: i.etat, ton: "neutre" as TonStatut };
                    return (
                      <TableRow key={i.id} data-testid={`invitation-${i.id}`}>
                        <TableCell><b>{i.email}</b></TableCell>
                        <TableCell>{i.organisation ?? "—"}</TableCell>
                        <TableCell>
                          <Pastille libelle={e.libelle} ton={e.ton} />
                          {i.etat === "UTILISEE" && i.consommeeLe && (
                            <Typography sx={{ fontSize: 12, color: "var(--c-text-secondary)" }}>le {jour(i.consommeeLe)}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {jour(i.creeeLe)}
                          {i.creeePar && <Typography sx={{ fontSize: 12, color: "var(--c-text-secondary)" }}>par {i.creeePar}</Typography>}
                        </TableCell>
                        <TableCell>{jour(i.expireLe)}</TableCell>
                        <TableCell align="right">
                          {i.etat === "EN_ATTENTE" && (
                            <BoutonSecondaire size="small" onClick={() => { setErreurAnnulation(null); setAAnnuler(i); }}>Annuler</BoutonSecondaire>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Bloc>
        </Box>
      )}

      <Dialog open={aAnnuler !== null} onClose={() => setAAnnuler(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Annuler l'invitation de {aAnnuler?.email}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            Le lien envoyé ne permettra plus de créer le compte. Vous pourrez inviter de nouveau cette adresse.
          </Typography>
          <Erreur message={erreurAnnulation} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <BoutonSecondaire onClick={() => setAAnnuler(null)}>Garder l'invitation</BoutonSecondaire>
          <BoutonPrincipal onClick={annuler}>Annuler l'invitation</BoutonPrincipal>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
